import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import type { Permission } from "@/lib/api/roles";

interface PermissionGridProps {
  permissions: Permission[];
  selectedIds: Set<string>;
  onToggle: (permissionId: string) => void;
  lockedIds?: Set<string>;
}

export function PermissionGrid({
  permissions,
  selectedIds,
  onToggle,
  lockedIds,
}: PermissionGridProps) {
  // Group permissions by resource, collect unique actions
  const { groupedByResource, allActions } = useMemo(() => {
    const resourceMap = new Map<string, Map<string, Permission>>();
    const actionSet = new Set<string>();

    for (const perm of permissions) {
      actionSet.add(perm.action);
      if (!resourceMap.has(perm.resource)) {
        resourceMap.set(perm.resource, new Map());
      }
      resourceMap.get(perm.resource)!.set(perm.action, perm);
    }

    // Known actions rendered first in a logical order; unknown ones appended alphabetically
    const actionOrder = [
      "manage-all",
      "create",
      "read",
      "update",
      "delete",
      "share",
      "hide",
      "rsvp",
      "assign",
      "unassign",
    ];
    const knownActions = actionOrder.filter((a) => actionSet.has(a));
    const unknownActions = [...actionSet]
      .filter((a) => !actionOrder.includes(a))
      .sort();
    const sortedActions = [...knownActions, ...unknownActions];

    // Sort resources alphabetically
    const sortedResources = [...resourceMap.entries()].sort(([a], [b]) =>
      a.localeCompare(b),
    );

    return {
      groupedByResource: sortedResources,
      allActions: sortedActions,
    };
  }, [permissions]);

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            <th className="sticky left-0 bg-muted/50 px-4 py-2 text-left font-medium">
              Resource
            </th>
            {allActions.map((action) => (
              <th
                key={action}
                className="px-3 py-2 text-center font-medium capitalize"
              >
                {action}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupedByResource.map(([resource, actionMap]) => (
            <tr key={resource} className="border-b last:border-b-0">
              <td className="sticky left-0 bg-background px-4 py-2 font-mono text-xs">
                {resource}
              </td>
              {allActions.map((action) => {
                const perm = actionMap.get(action);
                if (!perm) {
                  return (
                    <td key={action} className="px-3 py-2 text-center">
                      <span className="text-muted-foreground/30">-</span>
                    </td>
                  );
                }
                const isLocked = lockedIds?.has(perm.id) ?? false;
                return (
                  <td key={action} className="px-3 py-2 text-center">
                    <Checkbox
                      checked={selectedIds.has(perm.id)}
                      onCheckedChange={() => onToggle(perm.id)}
                      disabled={isLocked}
                      title={isLocked ? "This permission cannot be removed from this role" : undefined}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
